import React, { useState, useEffect, useCallback, useRef } from 'react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import styled from 'styled-components';
import { useToast } from '../Context/ToastContext';
import ReviewFindings from '../Components/TestBench/ReviewFindings';

/**
 * TestBenchPage — Brain Test Bench (admin/dev).
 * Pick category → program → generate an Opus scenario (or reuse) → run AI-vs-AI against the
 * category's real widget/brain. Optionally run TWO widget models side-by-side on the same
 * scenario; when both finish, an Opus judge (system-aware) says which model is better AND what
 * to improve in the brain (opportunity maps / directive / teachings). Human reviews; no auto-fix.
 */

const VARIANTS = ['typical', 'eligible', 'borderline', 'ineligible'];
const WIDGET_MODELS = ['brain default', 'gpt-5.6-terra', 'gpt-6-luna', 'gpt-6.1-sol', 'claude-sonnet-5-5', 'claude-opus-5-5'];
const MODEL_B_OPTIONS = ['none', ...WIDGET_MODELS];
// Reasoning effort for the GPT widget models. 'default' = derived from temperature (legacy).
// Lower = faster/cheaper — best for a scripted widget. Applies to both A and B.
const REASONING_OPTS = ['default', 'minimal', 'low', 'medium', 'high'];

export default function TestBenchPage() {
  const axios = useAuthenticatedAxios();
  const toast = useToast();

  const [taxonomy, setTaxonomy] = useState([]);
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [bestBrain, setBestBrain] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState('');
  // WHOSE widget the run drives: the category's main brain (iMaven's own agent for it) or any
  // real adviser / storefront in the discipline. Every agent owns its brain, directive and CRM
  // model settings, so each is tested on its own. Empty = the main brain.
  const [targets, setTargets] = useState([]);
  const [targetExpertId, setTargetExpertId] = useState('');

  const [savedScenarios, setSavedScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState('');
  const [variant, setVariant] = useState('typical');
  const [userModel, setUserModel] = useState(''); // empty = the CRM "Test Bench" model for the category (default Claude Opus 4.8)
  const [widgetModel, setWidgetModel] = useState('brain default');   // Model A
  const [widgetModelB, setWidgetModelB] = useState('none');          // Model B (compare)
  const [widgetReasoning, setWidgetReasoning] = useState('default'); // reasoning effort for A
  const [widgetReasoningB, setWidgetReasoningB] = useState('default'); // reasoning effort for B (compare)
  const [maxTurns, setMaxTurns] = useState(30);

  const [generating, setGenerating] = useState(false);
  const [runA, setRunA] = useState(null);
  const [runB, setRunB] = useState(null);
  const [starting, setStarting] = useState(false);
  const [judgment, setJudgment] = useState(null);
  const [judging, setJudging] = useState(false);
  const pollA = useRef(null), pollB = useRef(null);

  const cat = taxonomy.find(c => c.slug === category);
  const compareMode = widgetModelB && widgetModelB !== 'none';

  useEffect(() => {
    axios.get('/testbench/taxonomy').then(r => setTaxonomy(r.data?.taxonomy || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => { clearInterval(pollA.current); clearInterval(pollB.current); }, []);

  useEffect(() => {
    setProgramId('');
    if (!category) { setPrograms([]); setBestBrain(null); setTargets([]); setTargetExpertId(''); return; }
    const params = new URLSearchParams({ category }); if (subcategory) params.set('subcategory', subcategory);
    axios.get(`/testbench/targets?${params}`).then(r => {
      const list = r.data?.targets || [];
      setTargets(list);
      // The server picks the default: the main brain (iMaven's agent) when it has programs,
      // otherwise a real adviser — e.g. staffing runs against YK, whose positions live only in
      // YK's own brain.
      setTargetExpertId((list.find(t => t.isDefaultTarget) || list.find(t => t.isBestBrain))?.expertId || '');
    }).catch(() => setTargets([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory]);

  // Programs belong to the TARGET: every agent owns its brain, so each has its own pathways
  // (a labour staffing agency's POSITIONS live only in the agency's brain). So programs reload
  // whenever the target changes.
  useEffect(() => {
    setProgramId('');
    if (!category) { setPrograms([]); setBestBrain(null); return; }
    const params = new URLSearchParams({ category });
    if (subcategory) params.set('subcategory', subcategory);
    if (targetExpertId) params.set('targetExpertId', targetExpertId);
    axios.get(`/testbench/programs?${params}`).then(r => {
      setBestBrain(r.data?.bestBrain || null); setPrograms(r.data?.programs || []);
    }).catch(e => toast?.error?.(e?.response?.data?.message || 'Failed to load programs'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory, targetExpertId]);

  useEffect(() => {
    setScenarioId('');
    if (!programId) { setSavedScenarios([]); return; }
    axios.get(`/testbench/scenarios?programId=${encodeURIComponent(programId)}`)
      .then(r => setSavedScenarios(r.data?.scenarios || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const generate = async () => {
    if (!programId) { toast?.error?.('Pick a program first'); return; }
    setGenerating(true);
    try {
      const r = await axios.post('/testbench/scenarios/generate', { category, subcategory, programId, variant, targetExpertId });
      const sc = r.data?.scenario;
      toast?.success?.(`Generated: ${sc.persona.name}`);
      setSavedScenarios(prev => [sc, ...prev]); setScenarioId(sc._id);
    } catch (e) { toast?.error?.(e?.response?.data?.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const pollRun = (runId, setRun, ref) => {
    clearInterval(ref.current);
    ref.current = setInterval(async () => {
      try {
        const r = await axios.get(`/testbench/runs/${runId}`);
        const rn = r.data?.run; setRun(rn);
        if (rn?.status === 'done' || rn?.status === 'error') clearInterval(ref.current);
      } catch { /* keep polling */ }
    }, 2000);
  };

  const startRun = async () => {
    if (!scenarioId) { toast?.error?.('Generate or pick a scenario'); return; }
    setStarting(true); setRunA(null); setRunB(null); setJudgment(null);
    try {
      const ra = await axios.post('/testbench/runs', { scenarioId, userModel, widgetModel, widgetReasoning, maxTurns });
      pollRun(ra.data?.runId, setRunA, pollA);
      if (compareMode) {
        const rb = await axios.post('/testbench/runs', { scenarioId, userModel, widgetModel: widgetModelB, widgetReasoning: widgetReasoningB, maxTurns });
        pollRun(rb.data?.runId, setRunB, pollB);
      }
    } catch (e) { toast?.error?.(e?.response?.data?.message || 'Run failed to start'); }
    finally { setStarting(false); }
  };

  // When both runs finish (compare mode), auto-run the judge.
  useEffect(() => {
    if (!compareMode || judgment || judging) return;
    if (runA?.status === 'done' && runB?.status === 'done') {
      setJudging(true);
      axios.post('/testbench/compare', { runIdA: runA._id, runIdB: runB._id })
        .then(r => setJudgment(r.data?.judgment))
        .catch(e => toast?.error?.(e?.response?.data?.message || 'Judge failed'))
        .finally(() => setJudging(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runA, runB, compareMode, judgment, judging]);

  const selectedScenario = savedScenarios.find(s => s._id === scenarioId);
  const anyRun = runA || runB;

  return (
    <Page><Wrap $wide={compareMode && anyRun}>
      <H1>Brain Test Bench</H1>
      <Lead>Pick a category → program, let Opus build an applicant, and watch it converse with the
        category's real widget/brain. Run two models side-by-side to compare. Human reviews — nothing changes automatically.</Lead>

      {/* Selection */}
      <Card>
        <Grid>
          <Field><Label>Category</Label>
            <Select value={category} onChange={e => { setCategory(e.target.value); setSubcategory(''); }}>
              <option value="">Select…</option>
              {taxonomy.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </Select>
          </Field>
          <Field><Label>{cat?.subLabel || 'Subcategory'}</Label>
            <Select value={subcategory} onChange={e => setSubcategory(e.target.value)} disabled={!cat}>
              <option value="">{cat?.subRequired ? 'Select…' : '(none)'}</option>
              {(cat?.subcategories || []).map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </Select>
          </Field>
        </Grid>
        {bestBrain === null && category && <Muted>No iMaven agent exists for this category yet.</Muted>}
        {bestBrain && (
          <Muted>
            Main brain (iMaven agent): <b>{bestBrain.expertName}</b>
            {' · '}{programs.length} programs
            {targetExpertId && targetExpertId !== bestBrain.expertId && targets.length > 0 && (
              <> from <b>{targets.find(t => t.expertId === targetExpertId)?.expertName || targetExpertId}</b></>
            )}
          </Muted>
        )}
        {!!programs.length && (
          <Field><Label>Program</Label>
            <Select value={programId} onChange={e => setProgramId(e.target.value)}>
              <option value="">Select a program…</option>
              {programs.map(p => <option key={p.programId} value={p.programId}>{p.programName} ({p.questionCount}q · {p.domain})</option>)}
            </Select>
          </Field>
        )}
        {targets.length > 1 && (
          <Field><Label>Test against</Label>
            <Select value={targetExpertId} onChange={e => setTargetExpertId(e.target.value)}>
              {targets.map(t => (
                <option key={t.expertId} value={t.expertId} disabled={!t.hasApiKey}>
                  {t.expertName}
                  {t.isBestBrain ? ' — main brain' : (t.isTemplate ? ' — template' : ' — adviser')}
                  {t.isDefaultTarget ? ' (default)' : ''}
                  {t.hasApiKey ? '' : ' (no widget key)'}
                </option>
              ))}
            </Select>
            <Muted>
              Each agent owns its brain: this picks whose widget the conversation runs against and
              whose programs are listed — their brain, directive and model settings. Defaults to the
              iMaven agent when it has programs, otherwise to a real adviser.
            </Muted>
          </Field>
        )}
      </Card>

      {/* Scenario */}
      {programId && (
        <Card>
          <Row>
            <Label style={{ margin: 0 }}>Scenario —</Label>
            <Select value={variant} onChange={e => setVariant(e.target.value)} style={{ width: 'auto' }}>
              {VARIANTS.map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
            <PrimaryBtn disabled={generating} onClick={generate}>{generating ? 'Generating…' : 'Generate with Opus'}</PrimaryBtn>
          </Row>
          {savedScenarios.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {savedScenarios.map(s => (
                <ScenarioRow key={s._id} $sel={s._id === scenarioId} onClick={() => setScenarioId(s._id)}>
                  <input type="radio" readOnly checked={s._id === scenarioId} />
                  <div><b>{s.persona?.name}</b> <Tag>{s.variant}</Tag> <Muted as="span">· runs: {s.runCount || 0}</Muted>
                    <div><Muted as="span">{s.persona?.goal}</Muted></div></div>
                </ScenarioRow>
              ))}
            </div>
          )}
          {selectedScenario && <ProfileBox>{selectedScenario.persona?.profile}</ProfileBox>}
        </Card>
      )}

      {/* Run controls */}
      {scenarioId && (
        <Card>
          <Row>
            <Field><Label>Widget model A</Label>
              <Select value={widgetModel} onChange={e => setWidgetModel(e.target.value)} style={{ width: 170 }}>
                {WIDGET_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
              </Select></Field>
            <Field><Label>Compare vs (B)</Label>
              <Select value={widgetModelB} onChange={e => setWidgetModelB(e.target.value)} style={{ width: 170 }}>
                {MODEL_B_OPTIONS.map(m => <option key={m} value={m}>{m === 'none' ? 'none (single run)' : m}</option>)}
              </Select></Field>
            <Field><Label>{compareMode ? 'Reasoning (A)' : 'Reasoning'}</Label>
              <Select value={widgetReasoning} onChange={e => setWidgetReasoning(e.target.value)} style={{ width: 120 }}>
                {REASONING_OPTS.map(r => <option key={r} value={r}>{r}</option>)}
              </Select></Field>
            {compareMode && (
              <Field><Label>Reasoning (B)</Label>
                <Select value={widgetReasoningB} onChange={e => setWidgetReasoningB(e.target.value)} style={{ width: 120 }}>
                  {REASONING_OPTS.map(r => <option key={r} value={r}>{r}</option>)}
                </Select></Field>
            )}
            <Field><Label>Applicant model</Label><TextInput value={userModel} placeholder="CRM setting" onChange={e => setUserModel(e.target.value.trim())} style={{ width: 160 }} /></Field>
            <Field><Label>Max turns</Label><TextInput type="number" value={maxTurns} onChange={e => setMaxTurns(parseInt(e.target.value || '30', 10))} style={{ width: 80 }} /></Field>
            <PrimaryBtn disabled={starting} onClick={startRun} style={{ alignSelf: 'flex-end' }}>{starting ? 'Starting…' : compareMode ? 'Run comparison' : 'Run test'}</PrimaryBtn>
          </Row>
          <Muted>{compareMode
            ? 'Runs the same scenario on both sides in parallel — pick different models, or the SAME model at different reasoning levels (A vs B). When both finish, Opus judges which side is better and what to improve in the brain.'
            : '“brain default” = the model this brain is configured with. Set “Compare vs (B)” to run two models side-by-side.'}</Muted>
        </Card>
      )}

      {/* Live runs */}
      {anyRun && (
        compareMode
          ? <Columns><Col><RunPanel run={runA} label={`A · ${widgetModel} · ${widgetReasoning}`} /></Col><Col><RunPanel run={runB} label={`B · ${widgetModelB} · ${widgetReasoningB}`} /></Col></Columns>
          : <RunPanel run={runA} label={widgetModel} />
      )}

      {/* Judge */}
      {compareMode && anyRun && (
        <Card>
          {judging && <Muted>⚖️ Opus is comparing both runs and analysing the brain…</Muted>}
          {!judging && !judgment && (runA?.status === 'error' || runB?.status === 'error') &&
            <Muted>⚠️ One run failed or stalled — click “Run comparison” again to get the judge’s verdict.</Muted>}
          {!judging && !judgment && runA?.status !== 'error' && runB?.status !== 'error' && (runA?.status !== 'done' || runB?.status !== 'done') &&
            <Muted>Waiting for both runs to finish, then Opus will judge…</Muted>}
          {judgment && <Judgment j={judgment} modelA={`${widgetModel} · ${widgetReasoning}`} modelB={`${widgetModelB} · ${widgetReasoningB}`} />}
        </Card>
      )}
    </Wrap></Page>
  );
}

// ── a single run's live panel ──
function RunPanel({ run, label }) {
  if (!run) return <Card><Muted>Starting {label}…</Muted></Card>;
  const turns = run.turns || [];
  return (
    <Card>
      <Row><Badge $l="model">{label}</Badge>
        {run.widgetReasoning && run.widgetReasoning !== 'default' && <Badge $l="cls">reasoning:{run.widgetReasoning}</Badge>}
        <StatusBadge $s={run.status}>{run.phase}</StatusBadge>
        {/* A run that ends without the brain emitting [DONE] produced no summary and no
            classification — it was cut off by the turn cap. Saying only "complete" made a
            truncated run look like a successful one, so the two are now named apart. */}
        <Muted as="span">{turns.length} turns · {(run.totalMs / 1000).toFixed(0)}s
          {run.status === 'done' && (run.done ? ' · concluded' : ' · stopped at turn cap (no summary)')}</Muted></Row>
      {run.error && <ErrBox>{run.error}</ErrBox>}
      {turns.map(t => (
        <Turn key={t.index}>
          <TMeta>#{t.index} <Badge $l={t.latencyMs > 15000 ? 'bad' : t.latencyMs > 8000 ? 'warn' : 'ok'}>{(t.latencyMs / 1000).toFixed(1)}s</Badge>
            {t.progress && <Badge $l="prog">{t.progress.current}/{t.progress.total}</Badge>}
            {t.classification && <Badge $l="cls">{t.classification}</Badge>}</TMeta>
          <Bubble $who="w" dangerouslySetInnerHTML={{ __html: t.assistant }} />
          {t.brain && <details><summary style={{ cursor: 'pointer', color: '#8aa0b4', fontSize: 12 }}>brain: {t.brain.primaryPathway || '—'}</summary>
            <Pre>{t.brain.nextQuestion ? `next: ${t.brain.nextQuestion}\n` : ''}{t.brain.gaps || ''}{t.brain.blockers || ''}</Pre></details>}
          {t.user && <Bubble $who="u">{t.user}</Bubble>}
        </Turn>
      ))}
      {run.phase === 'reviewing' && <Muted>Opus reviewing…</Muted>}
      {run.review && (
        <Review $v={run.review.verdict}>
          <b>{run.review.verdict === 'ok' ? '✅ OK' : '⚠️ Needs attention'}</b>
          <p>{run.review.summary}</p>
          <ReviewFindings review={run.review} />
        </Review>
      )}
    </Card>
  );
}

// ── comparative judgment ──
function Judgment({ j, modelA, modelB }) {
  if (j.error) return <ErrBox>Judge error: {j.error}</ErrBox>;
  const winner = j.betterModel === 'A' ? modelA : j.betterModel === 'B' ? modelB : 'Tie';
  return (
    <div>
      <WinnerBox>🏆 Better model: <b>{winner}</b>{j.headline ? ` — ${j.headline}` : ''}</WinnerBox>
      <p style={{ color: '#cdd9e5' }}>{j.summary}</p>
      {(j.perModel || []).length > 0 && (
        <Columns>
          {j.perModel.map((m, i) => (
            <Col key={i}><MiniCard><b>{m.model}</b>
              <div><Muted as="span">Strengths:</Muted> {m.strengths}</div>
              <div><Muted as="span">Weaknesses:</Muted> {m.weaknesses}</div></MiniCard></Col>
          ))}
        </Columns>
      )}
      {(j.brainSuggestions || []).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <b style={{ color: '#fff' }}>What to improve in the brain</b>
          {j.brainSuggestions.map((s, i) => (
            <Finding key={i} $s={s.severity || 'low'}>
              <span className="sev">{s.severity || 'low'} · {s.area}</span> <b>{s.title}</b>
              <div>{s.detail}</div>
            </Finding>
          ))}
        </div>
      )}
    </div>
  );
}

// ── styles ──
const Page = styled.div`display:flex;justify-content:center;width:100%;height:100%;background:linear-gradient(135deg,#0a0d12,#111820 50%,#0d1117);padding:32px;box-sizing:border-box;overflow-y:auto;`;
const Wrap = styled.div`width:100%;max-width:${p => p.$wide ? '1280px' : '900px'};color:#fff;padding-bottom:60px;`;
const H1 = styled.h1`font-size:24px;margin:0 0 6px;`;
const Lead = styled.p`color:#9fb0c0;line-height:1.5;margin:0 0 18px;max-width:760px;`;
const Card = styled.div`background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:14px;padding:18px;margin-bottom:16px;`;
const MiniCard = styled.div`background:rgba(0,0,0,0.25);border-radius:10px;padding:10px 12px;font-size:13px;line-height:1.5;`;
const Grid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;`;
const Columns = styled.div`display:flex;gap:16px;align-items:flex-start;flex-wrap:wrap;`;
const Col = styled.div`flex:1;min-width:340px;`;
const Field = styled.div`display:flex;flex-direction:column;`;
const Label = styled.div`font-size:12.5px;color:#8aa0b4;margin:0 0 6px;`;
const inputBase = `width:100%;box-sizing:border-box;background:rgba(0,0,0,0.35);border:1px solid rgba(255,255,255,0.12);border-radius:8px;color:#fff;padding:9px 11px;font-size:14px;outline:none;&:focus{border-color:#4f8cff;}`;
const Select = styled.select`${inputBase} color-scheme: dark; & option { background:#0d1117; color:#e6edf3; }`;
const TextInput = styled.input`${inputBase}`;
const Row = styled.div`display:flex;gap:12px;align-items:center;flex-wrap:wrap;`;
const Muted = styled.div`color:#7d8fa1;font-size:12.5px;margin-top:8px;`;
const PrimaryBtn = styled.button`background:linear-gradient(135deg,#4f8cff,#2f6fe0);color:#fff;border:none;border-radius:8px;padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer;&:disabled{opacity:.5;cursor:not-allowed;}`;
const ScenarioRow = styled.div`display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border:1px solid ${p => p.$sel ? '#4f8cff' : 'rgba(255,255,255,0.08)'};border-radius:10px;margin-bottom:6px;cursor:pointer;background:${p => p.$sel ? 'rgba(79,140,255,0.08)' : 'transparent'};`;
const Tag = styled.span`background:rgba(255,255,255,0.1);border-radius:20px;padding:1px 8px;font-size:11px;`;
const ProfileBox = styled.div`margin-top:10px;background:rgba(0,0,0,0.25);border-radius:8px;padding:10px 12px;font-size:13px;color:#cdd9e5;line-height:1.5;`;
const Turn = styled.div`margin:10px 0;padding-bottom:8px;border-bottom:1px solid rgba(255,255,255,0.05);`;
const TMeta = styled.div`font-size:11px;color:#94a3b8;margin-bottom:6px;display:flex;gap:6px;align-items:center;`;
const Badge = styled.span`font-size:10.5px;font-weight:600;border-radius:20px;padding:1px 8px;background:${p => ({ ok: '#16653433', warn: '#854d0e33', bad: '#991b1b44', prog: '#3730a344', cls: '#6b21a844', model: '#0e749044' }[p.$l] || '#333')};color:${p => ({ ok: '#4ade80', warn: '#fbbf24', bad: '#f87171', prog: '#a5b4fc', cls: '#d8b4fe', model: '#67e8f9' }[p.$l] || '#ccc')};`;
const Bubble = styled.div`padding:8px 12px;border-radius:10px;margin:4px 0;max-width:88%;font-size:13.5px;${p => p.$who === 'u' ? 'background:#1e3a5f;margin-left:auto;' : 'background:rgba(255,255,255,0.06);'}`;
const Pre = styled.pre`white-space:pre-wrap;font-size:11px;background:rgba(0,0,0,0.3);padding:8px;border-radius:6px;color:#9fb0c0;margin:6px 0;overflow-x:auto;`;
const StatusBadge = styled.span`font-size:12px;font-weight:600;border-radius:20px;padding:3px 12px;background:${p => p.$s === 'error' ? '#991b1b44' : p.$s === 'done' ? '#16653433' : '#3730a344'};color:${p => p.$s === 'error' ? '#f87171' : p.$s === 'done' ? '#4ade80' : '#a5b4fc'};`;
const ErrBox = styled.div`background:#991b1b22;border:1px solid #991b1b55;color:#fca5a5;padding:10px;border-radius:8px;margin:8px 0;font-size:13px;`;
const Review = styled.div`margin-top:14px;background:${p => p.$v === 'ok' ? 'rgba(16,185,129,0.08)' : 'rgba(251,191,36,0.08)'};border:1px solid ${p => p.$v === 'ok' ? '#10b98155' : '#fbbf2455'};border-radius:10px;padding:12px;`;
const Finding = styled.div`margin:8px 0;padding:8px 10px;background:rgba(0,0,0,0.2);border-left:3px solid ${p => ({ high: '#ef4444', medium: '#f59e0b', low: '#64748b' }[p.$s] || '#64748b')};border-radius:4px;font-size:13px;.sev{font-size:10.5px;text-transform:uppercase;color:#94a3b8;}`;
const WinnerBox = styled.div`background:rgba(16,185,129,0.12);border:1px solid #10b98155;border-radius:10px;padding:12px 14px;font-size:15px;margin-bottom:10px;`;
